import { Button, Modal, Radio, Tabs, notification } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import EmailForm from "../../components/Common/EmailForm";
import DeviceManagement from "../../components/data360LiveDeviceManagement/DeviceManagement";
import D360LiveSlogStatus from "../../components/data360_live_slog/slog_status";
// import Data360LiveStatusConfiguration from "../../components/data360live_status_configuration/data360LiveStatusConfiguration";
import PQRReportContent from "../../components/data360live_PRQ_Report/PQRReportContent";
import Data360LiveStatusConfiguration from "../../components/data360live_status_configuration/data360LiveStatusConfiguration";
import { radioList } from "../../helpers";
import { hasEditPermission } from "../../helpers/rabc-helper";
import { FETCH_PQR_REPORTS_REQUEST } from "../../redux/data360Live_PQR/types";
import {
  GENERATE_PASS_VIEW_REPORT,
  GENERATE_TLOG_REPORT,
  GENERATE_ZONE_VIEW_REPORT,
} from "../../redux/data360Live_Report/types";
import { fetchDevices } from "../../redux/deviceRegister/action";
import MapBox from "../data360Live-Map/MapBox";

let tabKeys = {
  logData: "logData",
  tableView: "tableView",
  mapView: "mapView",
  weldParameterView: "weldParameterView",
  StatusConfiguration: "StatusConfiguration",
  PQRReport: "PQRReport",
  DeviceManagement: "DeviceManagement",
};

const TabList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [selectedView, setSelectedView] = useState(() => {
    return location.pathname === "/data360Live/pqr-report"
      ? tabKeys.PQRReport
      : tabKeys.tableView;
  });
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState();
  const [emails, setEmails] = useState([]);
  const [selectedWeldNumbers, setSelectedWeldNumbers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const dispatch = useDispatch();

  const currentProject = useSelector(
    (state) => state?.data360LiveProjectReducer?.currentProject
  );
  const slogData = useSelector(
    (state) => state.data360LiveSlogReducer?.weldStatusList
  );
  const isLoading = useSelector(
    (state) => state.data360LiveReportReducer?.loading
  );

  const weldNumbers = useMemo(
    () => [...new Set((slogData || []).map((item) => item.weldNumber))],
    [slogData]
  );

  const reducerList = useSelector((state) => state);
  const canEdit = hasEditPermission(reducerList?.auth?.userDetails);

  const defaultActiveKey = useMemo(() => {
    if (location.pathname === "/data360Live/pqr-report") {
      return tabKeys.PQRReport;
    }
    return tabKeys.tableView;
  }, [location.pathname]);

  useEffect(() => {
    if (currentProject?.project_id) {
      dispatch({
        type: FETCH_PQR_REPORTS_REQUEST,
        payload: { project_id: currentProject.project_id },
      });
    }
  }, [currentProject?.project_id, dispatch]);

  const handleWeldNumberChange = (values) => {
    setSelectedWeldNumbers(values || []);
  };

  const handleDateFilterChange = (startDate, endDate) => {
    setStartDate(startDate || null);
    setEndDate(endDate || null);
  };

  const tabItems = [
    {
      key: tabKeys.tableView,
      label: "Log Data",
      children: isMapVisible ? (
        <MapBox />
      ) : (
        <D360LiveSlogStatus
          handleWeldNumberChange={handleWeldNumberChange}
          handleDateFilterChange={handleDateFilterChange}
        />
      ),
    },
    {
      key: tabKeys.weldParameterView,
      label: "Weld Parameters View",
    },
    {
      key: tabKeys.StatusConfiguration,
      label: "Status Configuration",
      children: <Data360LiveStatusConfiguration />,
    },
    ...(canEdit
      ? [
          {
            key: tabKeys.PQRReport,
            label: "PQR Report",
            children: <PQRReportContent />,
          },
        ]
      : []),
    {
      key: tabKeys.DeviceManagement,
      label: "Device Management",
      children: <DeviceManagement />,
    },
  ];

  const toggleTableMap = () => {
    setIsMapVisible((prev) => !prev);
  };

  const openDownloadModal = () => {
    setIsModalVisible(true);
  };

  const closeDownloadModal = () => {
    setIsModalVisible(false);
    setSelectedRadio();
    setEmails([]);
    setStartDate(null);
    setEndDate(null);
  };

  const handleRadioChange = (e) => {
    setSelectedRadio(e.target.value);
  };

  const handleDownload = () => {
    if (!currentProject?.project_id) {
      notification.error({
        message: "Project ID is missing",
      });
      return;
    }

    if (emails.length === 0) {
      notification.error({
        message: "Please enter at least one email address",
      });
      return;
    }

    const payload = {
      projectId: currentProject?.project_id,
      emailAddress: emails.join(","),
      ...(selectedWeldNumbers &&
        selectedWeldNumbers.length > 0 && {
          weldNumbers: selectedWeldNumbers.join(","),
        }),
      ...(startDate && { startDate: startDate }),
      ...(endDate && { endDate: endDate }),
    };

    switch (selectedRadio) {
      case radioList.radioButton.radioButtonFirst:
        dispatch({
          type: GENERATE_TLOG_REPORT,
          payload,
        });
        break;
      case radioList.radioButton.radioButtonSecond:
        dispatch({
          type: GENERATE_PASS_VIEW_REPORT,
          payload,
        });
        break;
      case radioList.radioButton.radioButtonThird:
        dispatch({
          type: GENERATE_ZONE_VIEW_REPORT,
          payload,
        });
        break;
      default:
        notification.error({
          message: "Please select a report type and weld numbers",
        });
        return;
    }

    closeDownloadModal();
  };

  const renderTabExtraContent = () => {
    return (
      <>
        {selectedView === tabKeys.tableView &&
          currentProject.weldAssignmentType === "Automatic" && (
            <Radio.Group
              onChange={toggleTableMap}
              className="custom-radio-group"
              value={isMapVisible}
            >
              <Radio.Button
                className={
                  !isMapVisible
                    ? "ant-radio-button-wrapper-checked"
                    : "ant-radio-button-wrapper"
                }
              >
                Table
              </Radio.Button>
              <Radio.Button
                className={
                  isMapVisible
                    ? "ant-radio-button-wrapper-checked"
                    : "ant-radio-button-wrapper"
                }
              >
                Map
              </Radio.Button>
            </Radio.Group>
          )}
        {slogData.length > 0 &&
          selectedView !== "StatusConfiguration" &&
          selectedView !== "PQRReport" &&
          selectedView !== "DeviceManagement" &&
          selectedView !== "weldParameterView" && (
            <>
              {canEdit && (
                <Button
                  type="primary"
                  className="reportPdfdownload"
                  onClick={openDownloadModal}
                >
                  {t("sLogData.generateReport")}
                </Button>
              )}
            </>
          )}
      </>
    );
  };

  const tabActions = {
    [tabKeys.weldParameterView]: () => {
      history.push("/weld-parameter-view");
    },
    [tabKeys.DeviceManagement]: () => {
      dispatch(fetchDevices());
    },
  };

  const handleViewChange = (activeKey) => {
    setSelectedView(activeKey);

    if (tabActions[activeKey]) {
      tabActions[activeKey]();
    }
  };

  return (
    <>
      <Tabs
        defaultActiveKey={defaultActiveKey}
        onChange={handleViewChange}
        items={tabItems}
        className="custom-tab"
        destroyInactiveTabPane={true}
        tabBarExtraContent={renderTabExtraContent()}
      />
      <Modal
        title="Download Report"
        open={isModalVisible}
        onCancel={closeDownloadModal}
        className="modified-report-modal-style"
        width={600}
        footer={[
          <Button key="cancel" onClick={closeDownloadModal}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={handleDownload}
            disabled={!selectedRadio || emails.length === 0 || isLoading}
            loading={isLoading}
          >
            {isLoading ? "Sending" : "Send"}
          </Button>,
        ]}
      >
        <Radio.Group
          onChange={handleRadioChange}
          value={selectedRadio}
          className="report-download"
          disabled={isLoading}
        >
          <Radio value={radioList.radioButton.radioButtonFirst}>
            {t("sLogData.tlogReport")}
          </Radio>
          <Radio value={radioList.radioButton.radioButtonSecond}>
            {t("sLogData.passemail")}
          </Radio>
          <Radio value={radioList.radioButton.radioButtonThird}>
            {t("sLogData.zoneemail")}
          </Radio>
        </Radio.Group>

        {selectedRadio ? (
          <EmailForm
            weldNumbers={weldNumbers}
            emails={emails}
            setEmails={setEmails}
            selectedWeldNumbers={selectedWeldNumbers}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default TabList;
